// WHAT: Component for filtering time tracking data by period with custom date range
// WHY:  Provides interface for selecting time period filters for aggregated views
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useFilter hook, react-datepicker for date selection
// RISKS:
//   - NOTE: Date picker styling may not match TracQ branding
//   - NOTE: Custom date range validation may be incomplete
//   - NOTE: Period options are hardcoded - may need updates

// eslint-disable-next-line import/no-named-as-default -- react-datepicker exports DatePicker as default
import DatePicker from "react-datepicker";

import { useFilter, type Period } from "./FilterContextProvider.shared";
import "react-datepicker/dist/react-datepicker.css";

/**
 * WHAT:
 *   Type guard function to validate if a string is a valid Period value.
 *
 * WHY:
 *   Ensures type safety when handling period selection changes.
 *
 * @param value - The string value to validate
 *
 * @returns {value is Period} True if value is a valid Period, false otherwise
 *
 */
const isValidPeriod = (value: string): value is Period => {
  return (
    value === "Today" ||
    value === "This Week" ||
    value === "Last Week" ||
    value === "This Month" ||
    value === "Last Month" ||
    value === "This Year" ||
    value === "Last Year" ||
    value === "Alltime" ||
    value === "Custom"
  );
};

/**
 * WHAT:
 *   Component that provides period filter selection with custom date range
 *   support for filtering time tracking data.
 *
 * WHY:
 *   Enables users to filter aggregated time tracking views by predefined
 *   periods or custom date ranges.
 *
 * BEHAVIOR:
 *   - Displays period selector dropdown
 *   - Shows date pickers when "Custom" period is selected
 *   - Resets custom dates when switching to predefined periods
 *   - Uses global filter state from useFilter hook
 *
 * RISKS:
 *   - NOTE: Date picker styling may not match TracQ branding
 *   - NOTE: Custom date range validation may be incomplete
 *   - NOTE: Period options are hardcoded - may need updates
 *
 * @returns {JSX.Element} The filter setting component
 *
 */
export const FilterSetting = () => {
  const { customEnd, customStart, period, setCustomEnd, setCustomStart, setPeriod } = useFilter();

  /**
   * WHAT:
   *   Handles period selection change and resets custom dates if needed.
   *
   * WHY:
   *   Ensures custom dates are cleared when switching to predefined periods.
   *
   * @param e - The change event from the select element
   *
   * @returns {void}
   *
   */

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (isValidPeriod(value)) {
      setPeriod(value);
    }

    // NOTE: Reset custom dates when switching to predefined period
    if (value !== "Custom") {
      setCustomStart(null);
      setCustomEnd(null);
    }
  };

  return (
    <div className="mb-4 flex flex-col gap-4">
      {/* Period-Auswahl */}
      <div className="flex items-center">
        <label className="mr-4 font-semibold text-slate-300" htmlFor="period">
          Filter by Period:
        </label>

        <select
          className="rounded-lg border border-tracq-bright-blue bg-slate-800 py-2 pl-4 pr-10 text-slate-200 focus:ring-2 focus:ring-tracq-bright-blue focus:border-tracq-bright-blue transition-all duration-200"
          id="period"
          onChange={handlePeriodChange}
          value={period}
        >
          <option value="Today">Today</option>
          <option value="This Week">This Week</option>
          <option value="Last Week">Last Week</option>
          <option value="This Month">This Month</option>
          <option value="Last Month">Last Month</option>
          <option value="This Year">This Year</option>
          <option value="Last Year">Last Year</option>
          <option value="Alltime">Alltime</option>
          <option value="Custom">Custom</option>
        </select>
      </div>

      {/* Date-Picker nur bei „Custom" */}
      {period === "Custom" && (
        <div className="flex gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Start Date:</label>
            <DatePicker
              className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-slate-200 focus:ring-2 focus:ring-tracq-bright-blue focus:border-tracq-bright-blue transition-all duration-200"
              dateFormat="yyyy-MM-dd"
              onChange={(date) => {
                setCustomStart(date);
              }}
              placeholderText="Select start date"
              selected={customStart}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">End Date:</label>
            <DatePicker
              className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-slate-200 focus:ring-2 focus:ring-tracq-bright-blue focus:border-tracq-bright-blue transition-all duration-200"
              dateFormat="yyyy-MM-dd"
              onChange={(date) => {
                setCustomEnd(date);
              }}
              placeholderText="Select end date"
              selected={customEnd}
            />
          </div>
        </div>
      )}
    </div>
  );
};
